import { getQueryGroupDetailsByHash } from "@/helpers/apis";
import classes from "./styles.module.scss";
import { useSearchParams } from "react-router-dom";
import { TooltipCore, TooltipTrigger, TooltipContent, TooltipProvider } from "@altimateai/lego";
import { useQuery } from "@tanstack/react-query";
import type { QueryGroupStats } from "./types";

interface Props {
  queryHash: string;
}

const QUERY_HASH_DETAILS_KEY = "queryHashDetails";

const QueryHashWithHover = ({ queryHash }: Props) => {
  const [searchParams] = useSearchParams();
  const period = searchParams.get("period") || "month";

  const { data, isLoading, refetch } = useQuery<QueryGroupStats>({
    queryKey: [QUERY_HASH_DETAILS_KEY, queryHash, period],
    queryFn: async () => {
      const response = await getQueryGroupDetailsByHash(queryHash, { period });
      if (!response) {
        throw new Error("Failed to fetch query group stats");
      }
      return response;
    },
    enabled: false, // Don't fetch on component mount
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  const tooltipContent = () => {
    if (isLoading) {
      return "Fetching query group stats...";
    }

    if (!data) {
      return "No data available";
    }

    return (
      <div className={classes.queryHash}>
        <div>Total runs: {data.num_runs}</div>
        <div>Total cost: {data.total_cost}</div>
        <div>Avg. execution time: {data.avg_execution_time}</div>
        <div>Max cost savings: {data.max_cost_savings}</div>
        <div>Max time savings: {data.max_time_savings}</div>
        <div>Last run time: {data.last_run_time} ago</div>
      </div>
    );
  };

  return (
    <TooltipProvider>
      <TooltipCore
        onOpenChange={(open: boolean) => {
          if (open) refetch();
        }}
      >
        <TooltipTrigger asChild>
          <span>{queryHash}</span>
        </TooltipTrigger>
        <TooltipContent onClick={e => e.stopPropagation()}>{tooltipContent()}</TooltipContent>
      </TooltipCore>
    </TooltipProvider>
  );
};

export default QueryHashWithHover;
